import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, User, Target, Activity, Calculator, Zap, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const SettingsPage = () => {
  const { user, getAuthHeaders, API, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [settings, setSettings] = useState({
    calorie_goal: 2000,
    protein_goal: 150,
    carbs_goal: 250,
    fat_goal: 65,
    activity_level: "moderate",
    weight: "",
    height: "",
    age: "",
    gender: "",
    fitness_goal: "maintain",
    coach_mode: false
  });
  const [calculationResult, setCalculationResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(prev => ({
        ...prev,
        ...response.data,
        weight: response.data.weight || "",
        height: response.data.height || "",
        age: response.data.age || "",
        gender: response.data.gender || "",
        fitness_goal: response.data.fitness_goal || "maintain",
        coach_mode: response.data.coach_mode || false
      }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const dataToSend = {
        ...settings,
        weight: settings.weight ? parseFloat(settings.weight) : null,
        height: settings.height ? parseFloat(settings.height) : null,
        age: settings.age ? parseInt(settings.age) : null,
        gender: settings.gender || null,
        fitness_goal: settings.fitness_goal || null,
        coach_mode: settings.coach_mode
      };
      
      await axios.put(`${API}/settings`, dataToSend, {
        headers: getAuthHeaders()
      });
      
      updateUser({ calorie_goal: settings.calorie_goal, coach_mode: settings.coach_mode });
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const calculateGoals = async () => {
    if (!settings.weight || !settings.height || !settings.age) {
      toast.error("Completa peso, altura y edad para calcular");
      return;
    }

    setCalculating(true);
    try {
      // First save the profile data
      await axios.put(`${API}/settings`, {
        weight: parseFloat(settings.weight),
        height: parseFloat(settings.height),
        age: parseInt(settings.age),
        gender: settings.gender || "male",
        activity_level: settings.activity_level,
        fitness_goal: settings.fitness_goal
      }, { headers: getAuthHeaders() });

      // Then calculate
      const response = await axios.post(`${API}/settings/calculate`, {}, {
        headers: getAuthHeaders()
      });

      setCalculationResult(response.data);
      
      // Update settings with calculated values
      setSettings(prev => ({
        ...prev,
        calorie_goal: response.data.calorie_goal,
        protein_goal: response.data.protein_goal,
        carbs_goal: response.data.carbs_goal,
        fat_goal: response.data.fat_goal
      }));

      toast.success("Calorías calculadas según tu objetivo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al calcular");
    } finally {
      setCalculating(false);
    }
  };

  const activityLevels = [
    { value: "sedentary", label: "Sedentario (poco o nada de ejercicio)" },
    { value: "light", label: "Ligero (ejercicio 1-3 días/semana)" },
    { value: "moderate", label: "Moderado (ejercicio 3-5 días/semana)" },
    { value: "active", label: "Activo (ejercicio 6-7 días/semana)" },
    { value: "very_active", label: "Muy activo (ejercicio intenso diario)" }
  ];

  const fitnessGoals = [
    { value: "lose_weight", label: "Perder peso", description: "Déficit calórico (-500 kcal)" },
    { value: "define", label: "Definir", description: "Déficit moderado, alta proteína" },
    { value: "maintain", label: "Mantener peso", description: "Calorías de mantenimiento" },
    { value: "gain_strength", label: "Ganar fuerza", description: "Superávit moderado (+250 kcal)" },
    { value: "gain_weight", label: "Ganar peso/masa", description: "Superávit calórico (+400 kcal)" }
  ];

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto p-6 space-y-6" data-testid="settings-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Configuración</h1>
          <p className="text-muted-foreground">Personaliza tus objetivos nutricionales</p>
        </div>

        {/* Coach Mode */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-destructive/10 to-accent/10 border border-destructive/20 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-destructive/20 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="font-heading font-semibold">Modo Coach Directo</h3>
                <p className="text-sm text-muted-foreground">Feedback sin filtros. Sin suavizar. La verdad directa.</p>
              </div>
            </div>
            <Switch
              checked={settings.coach_mode}
              onCheckedChange={(checked) => setSettings({ ...settings, coach_mode: checked })}
              data-testid="coach-mode-switch"
            />
          </div>
          {settings.coach_mode && (
            <div className="mt-4 p-3 bg-destructive/10 rounded-xl flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">
                El coach no te va a motivar. Te va a decir lo que necesitas oír, no lo que quieres oír.
              </p>
            </div>
          )}
        </motion.div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Perfil</h3>
              <p className="text-sm text-muted-foreground">Información para calcular tus calorías</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="weight">Peso (kg)</Label>
              <Input
                id="weight"
                type="number"
                placeholder="70"
                value={settings.weight}
                onChange={(e) => setSettings({ ...settings, weight: e.target.value })}
                className="h-12 rounded-xl"
                data-testid="weight-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="height">Altura (cm)</Label>
              <Input
                id="height"
                type="number"
                placeholder="175"
                value={settings.height}
                onChange={(e) => setSettings({ ...settings, height: e.target.value })}
                className="h-12 rounded-xl"
                data-testid="height-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="age">Edad</Label>
              <Input
                id="age"
                type="number"
                placeholder="30"
                value={settings.age}
                onChange={(e) => setSettings({ ...settings, age: e.target.value })}
                className="h-12 rounded-xl"
                data-testid="age-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">Género</Label>
              <Select
                value={settings.gender}
                onValueChange={(value) => setSettings({ ...settings, gender: value })}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="gender-select">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Hombre</SelectItem>
                  <SelectItem value="female">Mujer</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Activity Level */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Nivel de actividad</h3>
              <p className="text-sm text-muted-foreground">Tu actividad física habitual</p>
            </div>
          </div>

          <Select
            value={settings.activity_level}
            onValueChange={(value) => setSettings({ ...settings, activity_level: value })}
          >
            <SelectTrigger className="h-12 rounded-xl" data-testid="activity-level-select">
              <SelectValue placeholder="Selecciona tu nivel de actividad" />
            </SelectTrigger>
            <SelectContent>
              {activityLevels.map((level) => (
                <SelectItem key={level.value} value={level.value}>
                  {level.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Fitness Goal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Tu objetivo</h3>
              <p className="text-sm text-muted-foreground">¿Qué quieres conseguir?</p>
            </div>
          </div>

          <div className="space-y-3">
            {fitnessGoals.map((goal) => (
              <button
                key={goal.value}
                onClick={() => setSettings({ ...settings, fitness_goal: goal.value })}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  settings.fitness_goal === goal.value
                    ? "border-primary bg-primary/5"
                    : "border-border/50 hover:border-border"
                }`}
                data-testid={`goal-${goal.value}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{goal.label}</p>
                    <p className="text-sm text-muted-foreground">{goal.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    settings.fitness_goal === goal.value 
                      ? "border-primary bg-primary" 
                      : "border-muted-foreground"
                  }`}>
                    {settings.fitness_goal === goal.value && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-full" />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Calculate Button */}
          <Button
            onClick={calculateGoals}
            disabled={calculating}
            variant="secondary"
            className="w-full mt-4 h-12 rounded-xl"
            data-testid="calculate-btn"
          >
            <Calculator className="w-5 h-5 mr-2" />
            {calculating ? "Calculando..." : "Calcular calorías según mi objetivo"}
          </Button>

          {/* Calculation Result */}
          {calculationResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-xl"
            >
              <p className="text-sm font-medium text-primary mb-2">{calculationResult.description}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Metabolismo basal:</span>
                  <span className="font-mono ml-1">{calculationResult.bmr} kcal</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Gasto diario:</span>
                  <span className="font-mono ml-1">{calculationResult.tdee} kcal</span>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Goals Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Objetivos diarios</h3>
              <p className="text-sm text-muted-foreground">Ajusta manualmente o usa el calculador</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="calories">Objetivo de calorías (kcal)</Label>
              <Input
                id="calories"
                type="number"
                value={settings.calorie_goal}
                onChange={(e) => setSettings({ ...settings, calorie_goal: parseInt(e.target.value) || 0 })}
                className="h-12 rounded-xl"
                data-testid="calorie-goal-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="protein">Proteína (g)</Label>
              <Input
                id="protein"
                type="number"
                value={settings.protein_goal}
                onChange={(e) => setSettings({ ...settings, protein_goal: parseInt(e.target.value) || 0 })}
                className="h-12 rounded-xl"
                data-testid="protein-goal-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="carbs">Carbohidratos (g)</Label>
              <Input
                id="carbs"
                type="number"
                value={settings.carbs_goal}
                onChange={(e) => setSettings({ ...settings, carbs_goal: parseInt(e.target.value) || 0 })}
                className="h-12 rounded-xl"
                data-testid="carbs-goal-input"
              />
            </div>
            <div className="space-y-2 col-span-2 sm:col-span-1">
              <Label htmlFor="fat">Grasas (g)</Label>
              <Input
                id="fat"
                type="number"
                value={settings.fat_goal}
                onChange={(e) => setSettings({ ...settings, fat_goal: parseInt(e.target.value) || 0 })}
                className="h-12 rounded-xl"
                data-testid="fat-goal-input"
              />
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full h-12 rounded-xl"
            data-testid="save-settings-btn"
          >
            <Save className="w-5 h-5 mr-2" />
            {loading ? "Guardando..." : "Guardar Configuración"}
          </Button>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
